import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { gsap } from "gsap";
import { meshList } from './scene/meshList.js';

class Scene {
	constructor() {
		this.canvas = document.querySelector('#scene canvas');
		this.scene = new THREE.Scene();
		this.renderer = null;
		this.camera = null;
		this.clock = new THREE.Clock();
		this.textures = {};
		this.rotatingMeshes = {};
		this.flyBys = [];
		this.initialPositions = [];
		this.animationId = null;
		
		// Screen dimensions
		this.screen = {
			width: document.documentElement.clientWidth,
			height: document.documentElement.clientHeight
		};

		// Initialize loaders
		this.dracoLoader = new DRACOLoader();
		this.dracoLoader.setDecoderPath('/3d/draco/');
		
		this.gltfLoader = new GLTFLoader();
		this.gltfLoader.setDRACOLoader(this.dracoLoader);
		
		this.textureLoader = new THREE.TextureLoader();
		
		// Lighting
		this.ambientLight = null;
		this.directionalLight = null;
		
		// Mesh configuration
		this.meshList = meshList;
		
	}

	// Utility methods
	generateRandom(max, min = 0) {
		return Math.random() * (max - min) + min;
	}

	// Create vertical gradient texture
	createGradientTexture(colors, stops = []) {
		const canvas = document.createElement('canvas');
		canvas.width = 256;
		canvas.height = 256;
		const ctx = canvas.getContext('2d');

		// Create vertical gradient (top to bottom)
		const gradient = ctx.createLinearGradient(256, 256, 256, 0);

		// Add color stops
		colors.forEach((color, index) => {
			const stop = stops[index] !== undefined ? stops[index] : index / (colors.length - 1);
			gradient.addColorStop(stop, `#${color.toString(16).padStart(6, '0')}`);
		});

		ctx.fillStyle = gradient;
		ctx.fillRect(0, 0, 256, 256);

		const texture = new THREE.CanvasTexture(canvas);
		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		return texture;
	}

	// Create and configure alphaMap texture
	createAlphaMap(alphaMapPath) {
		const alphaTexture = this.textureLoader.load(`/3d/${alphaMapPath}.png`);
		alphaTexture.flipY = false;
		alphaTexture.wrapS = THREE.RepeatWrapping;
		alphaTexture.wrapT = THREE.ClampToEdgeWrapping;
		alphaTexture.repeat.set(1, 1);
		alphaTexture.offset.set(0.5 - 0.5 / alphaTexture.repeat.x, 0.5 - 0.5 / alphaTexture.repeat.y);
		alphaTexture.repeat.set(1, 1);
		return alphaTexture;
	}




	// Give the train a judder to create realistic movement
	animateTrain(object) {
		gsap.to(object.position, {
			keyframes: [
				{ x: object.position.x, y: object.position.y, z: object.position.z, duration: 0 },
				{ x: object.position.x - 0.0005, y: object.position.y - 0.005, z: object.position.z - 0.0005, duration: 0.9 },
				{ x: object.position.x + 0.0005, y: object.position.y + 0.0025, z: object.position.z + 0.0025, duration: 0.9 },
				{ x: object.position.x - 0.0025, y: object.position.y - 0.005, z: object.position.z - 0.0005, duration: 0.9 },
				{ x: object.position.x + 0.0005, y: object.position.y + 0.0025, z: object.position.z + 0.0025, duration: 0.9 },
				{ x: object.position.x - 0.0025, y: object.position.y - 0.005, z: object.position.z - 0.0005, duration: 0.9 },
			],
			repeat: -1,
			yoyo: true,
		});
	}

	// Initialize the scene
	init() {
		if (!this.canvas) {
			document.documentElement.classList.add('has-fallback-hero');
			return;
		}

		this.setupCamera();
		this.setupRenderer();
		this.setupLighting();
		this.loadModel();
		this.setupEventListeners();
		this.tick();
	}

	// Load the 3D model
	loadModel() {
		this.gltfLoader.load('/3d/train.glb', (gltf) => {
			const model = gltf.scene;

			document.documentElement.classList.add('has-hero-visible');
		
			model.traverse((child) => {
				if (child.isMesh) {
					for (let key in this.meshList) {
						if (child.name === this.meshList[key].name) {
							if(this.meshList[key].hasOwnProperty('flyBy')) {
								this.flyBys.push(child);
							}


							// Apply the correct colour to the relevant mesh
							if (this.meshList[key].gradient) {
								// Create gradient texture
								const gradientTexture = this.createGradientTexture(
									this.meshList[key].gradient.colors,
									this.meshList[key].gradient.stops
								);
								
								// Create material with gradient texture
								const materialProps = {
									map: gradientTexture,
									transparent: this.meshList[key].transparent ? true : false,
									opacity: 1,
								};

								// Add alphaMap if specified
								if (this.meshList[key].alphaMap) {
									materialProps.alphaMap = this.createAlphaMap(this.meshList[key].alphaMap);
									materialProps.transparent = true;
									materialProps.alphaTest = 0.5;
								}

								child.material = new THREE.MeshStandardMaterial(materialProps);
							} else if (this.meshList[key].color) {
								const materialProps = {
									color: this.meshList[key].color,
									side: this.meshList[key].doubleSided ? THREE.DoubleSide : THREE.FrontSide,
									opacity: 1,
								};

								if (this.meshList[key].alphaMap) {
									materialProps.alphaMap = this.createAlphaMap(this.meshList[key].alphaMap);
									materialProps.transparent = true;
									materialProps.alphaTest = 0.5;
									child.material = new THREE.MeshStandardMaterial(materialProps);
								} else {
									child.material = new THREE.MeshToonMaterial(materialProps);
								}
							} else if (this.meshList[key].texture) {
								/* Load the texture  */
								this.textures[key] = this.textureLoader.load(`/3d/${this.meshList[key].texture}.${this.meshList[key].ext ? this.meshList[key].ext : 'png'}`);
								this.textures[key].flipY = false;
								this.textures[key].wrapS = THREE.RepeatWrapping;
								this.textures[key].wrapT = THREE.ClampToEdgeWrapping;
								this.textures[key].repeat.set(1, 1);
		
								// Create material with texture
								const materialProps = {
									map: this.textures[key],
									transparent: this.meshList[key].transparent ? true : false,
									opacity: 1,
								};

								// Add alphaMap if specified
								if (this.meshList[key].alphaMap) {
									materialProps.alphaMap = this.createAlphaMap(this.meshList[key].alphaMap);
									materialProps.transparent = true;
									materialProps.alphaTest = 0.5;
								}

								/* Apply the texture and enable transparent if needed */
								child.material = new THREE.MeshStandardMaterial(materialProps);
								
								// Force proper rendering order for transparent objects
								if (this.meshList[key].transparent) {
									child.renderOrder = 1;
								}
							}
		
							/* Enabled shadow casting and receive on the mesh */
							child.castShadow = this.meshList[key].castShadows;
							child.receiveShadow = this.meshList[key].receiveShadows;
							
							/* Apply Y position if specified */
							if (this.meshList[key].position && this.meshList[key].position.y !== undefined) {
								child.position.y = this.meshList[key].position.y;
							}
							
							/* If the key contains `rotate: true` add it to the rotatingMeshes object */
							if (this.meshList[key].rotate) {
								this.rotatingMeshes[key] = {
									mesh: child,
									...this.meshList[key]
								};
							}
							
							/* If the key contains `judder: true` give it a judder animation */
							if (this.meshList[key].judder) {
								this.animateTrain(child);
							}
					}

					}
				}
			});
		
			this.scene.add(model);
	
			/* Loading camera animations */
			gsap.to(this.camera.position, {
				x: -0.816,
				z: 1.195,
				duration: 3.5,
				ease: "power3.out",
				delay: .3,
				onUpdate: () => {
					this.renderer.render(this.scene, this.camera);
				}
			});
			gsap.to(this.camera.rotation, {
				y: -0.816,
				duration: 3.5,
				ease: "power3.out",
				delay: .3,
				onUpdate: () => {
					this.renderer.render(this.scene, this.camera);
				}
			});
		
			/* Loading tone mapping exposure animation (Fade from black) */
			gsap.to(this.renderer, {
				toneMappingExposure: 1.452,
				duration: 2.5,
				ease: "power1.out",
				delay: .3,
				onUpdate: () => {
					this.renderer.render(this.scene, this.camera);
				}
			});

			/* Continuous directional light animations. Mimics the sun by increasing the x position over time */
			gsap.to(this.directionalLight, {
				intensity: 3.9,
				duration: 4,
				ease: "power1.out",
				delay: .3,
				onUpdate: () => {
					this.renderer.render(this.scene, this.camera);
				}
			});

			/* Animate the flyby meshes. This gives the affect of the train travelling past lamp posts and buildings */
			/* Store the initial position of each mesh. This allows us to move the mesh back to its original position before the animation runs again */
			this.initialPositions = this.flyBys.map(item => item.position.clone());
			this.animateFlyBys();
		});
	}

	// Animate the flyby meshes
	animateFlyBys() {
		if (this.flyBys.length) {
			// Provide a random index to select a random item from the flyBys array
			const randomIndex = Math.floor(Math.random() * this.flyBys.length);
			const item = this.flyBys[randomIndex];
			
			// Reset the position to the initial position before starting the animation
			item.position.copy(this.initialPositions[randomIndex]);
			
			// Rerun the animation on completion
			gsap.to(item.position, {
				x: item.position.x - 18,
				duration: 1.2,
				ease: 'linear',
				delay: 4,
				repeat: 0,
				onComplete: () => {
					this.animateFlyBys();
				}
			});
		}
	}

	// Set the scene aspect ratio based on screen dimensions
	setAspectRatio() {
		if (!this.canvas || !this.renderer) {
			return false;
		}

		/* Update screen size */
		const width = window.innerWidth;
		const height = window.innerHeight;

		/* Update camera; zoom in if aspect ratio is too wide */
		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();

		/* Update renderer */
		this.renderer.setSize(width, height);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.render(this.scene, this.camera);
	}

	// Setup lighting
	setupLighting() {
		/* Create lighting */
		this.ambientLight = new THREE.AmbientLight(0xfff0f0, 3.6);
		this.scene.add(this.ambientLight);

		/* This light will mimic the sun */
		this.directionalLight = new THREE.DirectionalLight(0xdfd6ff, 0);
		this.directionalLight.position.set(-1.78, 29.2, 63);
		const lightPos = this.directionalLight.position;

		/* Enable shadows from the directional light */
		this.directionalLight.castShadow = true;
		this.directionalLight.shadow.mapSize.width = 4096; // Increase shadow map size for better quality
		this.directionalLight.shadow.mapSize.height = 4096;
		this.scene.add(this.directionalLight);

		/* Animate the directional light to create organic vertical movements */
		this.animateLight(lightPos);
	}

	// Animate the directional light
	animateLight(lightPos) {
		/* Rerun the animation on completion */
		gsap.to(this.directionalLight.position, {
			keyframes: [
				{ y: lightPos.y },
				{ y: lightPos.y + this.generateRandom(0.7), duration: this.generateRandom(1.2, 0.3) },
				{ y: lightPos.y - this.generateRandom(0.7), duration: this.generateRandom(1.2, 0.3) },
				{ y: lightPos.y + this.generateRandom(0.7), duration: this.generateRandom(1.2, 0.3) },
				{ y: lightPos.y - this.generateRandom(0.7), duration: this.generateRandom(1.2, 0.3) },
				{ y: lightPos.y + this.generateRandom(0.7), duration: this.generateRandom(1.2, 0.3) }
			],
				ease: 'linear',
				repeat: 1,
				yoyo: true,
				onComplete: () => this.animateLight(lightPos)
		});
	}

	// Setup camera
	setupCamera() {
		/* Create the camera */
		this.camera = new THREE.PerspectiveCamera(55, this.screen.width / this.screen.height);
		this.camera.position.set(-2, 0.905, 0.95);
		this.camera.rotation.order = 'YXZ';
		this.camera.rotation.set(0, -0.950, 0);
		this.scene.add(this.camera);
	}

	// Setup renderer
	setupRenderer() {
		/* Create renderer */
		this.renderer = new THREE.WebGLRenderer({
			canvas: this.canvas,
			antialias: true
		});
		
		// Enable shadow mapping
		this.renderer.shadowMap.enabled = true;
		// PCFSoftShadowMap for better quality shadows
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		
		/* Configure tone mapping */
		this.renderer.toneMapping = THREE.NeutralToneMapping;
		/* Start the exposure from 0 for the purpose of the loading animation */
		this.renderer.toneMappingExposure = 0;
		
		/* Set renderer config */
		this.renderer.outputColorSpace = THREE.SRGBColorSpace
		this.renderer.setSize(this.screen.width, this.screen.height);
		this.renderer.render(this.scene, this.camera);
	}

	// Setup event listeners
	setupEventListeners() {
		/* Deal with resizing windows */
		window.addEventListener('resize', () => this.setAspectRatio());
		this.setAspectRatio();
	}

	// Animation loop
	tick() {
		if (this.renderer) {
			// Get the elapsed time since the last frame
			const delta = this.clock.getDelta();

			// Move the directional light's x position over time
			this.directionalLight.position.x += 0.05 * delta;

			// Loop through the rotatingMeshes object
			for (let key in this.rotatingMeshes) {
				// Check if the object has a rotationSpeed and a mesh to rotate
				if (this.rotatingMeshes[key].rotationSpeed && this.rotatingMeshes[key].mesh) {
					// Rotate the mesh on the Y-axis by the amount in rotationSpeed, adjusted by delta
					this.rotatingMeshes[key].mesh.rotation.y += this.rotatingMeshes[key].rotationSpeed * delta;
				}
			}

			/* Parallax effect on scroll */
			this.camera.position.y = 0.905 - (window.scrollY * 0.00075);
			this.camera.rotation.x = window.scrollY * 0.00025;

			/* Render frame with above changes */
			this.renderer.render(this.scene, this.camera);

			/* Call tick again on the next frame */
			this.animationId = window.requestAnimationFrame(() => this.tick());
		}
	}
}

// Initialize the scene when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	const scene = new Scene();
	scene.init();
});
